import {
  IconAnalytics,
  IconBell,
  IconBinocularsGray,
  IconChainLink,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTvocGray,
  IconWarning,
  IconZoneGray,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import mainVideo from '@src/assets/images/solution/constructionSafety/video/rtlsVideo.mp4';

import image1 from '@src/assets/images/solution/publicFire/img/1.avif';
import image2 from '@src/assets/images/solution/publicFire/img/2.avif';
import image3 from '@src/assets/images/solution/publicFire/img/3.avif';
import image4 from '@src/assets/images/solution/publicFire/img/4.avif';

import customer1 from '@src/assets/images/solution/publicFire/img/customer1.avif';
import function1 from '@src/assets/images/solution/publicFire/img/function1.avif';
import function2 from '@src/assets/images/solution/publicFire/img/function2.avif';
import function3 from '@src/assets/images/solution/publicFire/img/function3.avif';
import function4 from '@src/assets/images/solution/publicFire/img/function4.avif';

export default {
  type: 'Firefighting Solution',

  banner: {
    title: 'Verify your firefighting training performance with data-driven insights.',
    desc: 'Track firefighters’ locations in real-time to objectively assess training accuracy and enhance operational efficiency.',
  },

  summary: {
    title: 'Digitalize precise evaluation and management of firefighting training.',
    desc: 'Analyze and efficiently manage training processes and results objectively using real-time firefighter location data.',
    function: {
      card: [
        {
          image: function1,
          title: 'Movement Path Heatmap Visualization',
          desc: 'Record detailed movement paths and trajectories of firefighters as precise data to evaluate whether training goals were achieved. Analyze training accuracy based on actual movement data and clearly identify areas for improvement.',
        },
        {
          image: function2,
          title: 'Detailed Movement Data Analysis',
          desc: 'Record detailed movement paths and trajectories of firefighters as precise data to evaluate whether training goals were achieved. Analyze training accuracy based on actual movement data and clearly identify areas for improvement.',
        },
        {
          image: function3,
          title: 'Ensure Accurate Location Data Reception',
          desc: 'TwinTracker enables real-time, error-free reception and recording of firefighter location data, allowing precise movement path and position analysis during training. Accurate data reception increases the reliability of training evaluation and supports effective operational management.',
        },
        {
          image: function4,
          title: 'Integrated Real-Time Operation Monitoring',
          desc: 'Monitor the entire training area in real-time via installed TwinTrackers, quickly grasp training progress, and enhance operational efficiency and response speed.',
        },
      ],
    },
  },

  numOfCard: {
    title: 'Maximize operational efficiency with data-driven firefighting training evaluation',
    desc: 'Improve on-site operations accurately and effectively with objective training data.',
    card: [
      {
        tag: '97%',
        title: 'Automatic Training Path Logging Accuracy',
        desc: 'Record firefighters’ movement paths and locations in real-time with high accuracy to maximize training evaluation reliability.',
      },
      {
        tag: '95%',
        title: 'Real-Time Tracking & Monitoring Coverage',
        desc: 'Cover every zone and personnel within the training facility without omission for flawless real-time monitoring.',
      },
      {
        tag: '99%',
        title: 'Site & Digital Map Structural Consistency',
        desc: 'Precisely implement the site structure on the digital map to ensure perfect consistency with the actual training site.',
      },
    ],
  },

  func: [
    {
      theme: 'Enhance training accuracy using firefighter tracking features.<br/>',
      title: 'Site-Optimized TwinTracker Installation for Precise Tracking',
      desc: 'Analyze the actual structure of the training site, including subway cars, stairs, and corridors, and install TwinTrackers at optimal locations. Careful placement and efficient configuration tailored to each site enable precise, error-free tracking of firefighter locations.',
      sub1: 'High Accuracy',
      icon1: IconRulerGray,
      sub2: 'Real-Time Status Monitoring',
      icon2: IconClockGray,
      image: image1,
    },
    {
      title: 'Accurate Data Reception via On-Site TwinTrackers',
      desc: 'TwinTrackers installed on-site reliably receive accurate location data without signal interference, even inside subway cars and complex structures. High reception accuracy provides trustworthy real-time location information of firefighters.',
      sub2: 'Real-Time Location Tracking',
      icon1: IconBinocularsGray,
      sub1: 'Fast Data Integration',
      icon2: IconTvocGray,
      image: image2,
    },
    {
      title: 'Digital Location Tracking Environment Based on Site Structure',
      desc: 'Precisely replicate the actual site structure and subway cars on a digital map to intuitively monitor firefighter movements. Accurately reflect site structures and obstacles to increase consistency between the real site and the digital interface while securing accurate real-time location data.',
      sub1: 'Real-Time Location Tracking',
      icon1: IconBinocularsGray,
      sub2: 'High Accuracy',
      icon2: IconRulerGray,
      image: image3,
    },
    {
      title: 'Consistent Tracking Solution from Blueprints to 3D Visualization',
      desc: 'TwinTracker installation is based on precise design blueprints, and installation status can be clearly verified through actual site photos. This connects to an intuitive monitoring screen and realistic 3D visualization, providing a consistent and clear location tracking solution from installation to operation.',
      sub1: 'Flexible Zone Configuration',
      icon1: IconZoneGray,
      sub2: 'Multiple Zone Settings',
      icon2: IconTagGray,
      image: image4,
    },
  ],

  customer: {
    title: 'Hear real reviews from customers who chose ORBRO',
    card: [
      {
        logo: customer1,
        desc: 'Until now, firefighting training was mainly evaluated based on experience and intuition. Thanks to ORBRO’s real-time tracking system, we can now accurately identify firefighters’ training processes and movement paths. Being able to verify training results with objective data has greatly increased the reliability and efficiency of evaluations. We expect it will also significantly help in actual field operations.',
        name: 'Seoul Metropolitan Fire & Disaster Headquarters',
      },
    ],
  },

  subfunc: {
    title: 'Key Feature Summary',
    desc: 'Ensuring worker safety enhances efficiency and reduces accident-related losses. ORBRO provides optimal features to protect workers on construction sites. The system is designed to appropriately respond to safety incidents, whether workers approach danger zones, an accident occurs, or there are potential hazards involving heavy machinery.',

    data: [
      {
        icon: IconLocation,
        title: 'Real-Time Location Tracking',
        desc: 'Accurately track firefighters’ locations in real-time within the site.',
      },
      {
        icon: IconChartBar,
        title: 'Precise Installation & Data Reception',
        desc: 'Secure accurate and stable location data through site-optimized installation.',
      },
      {
        icon: IconAnalytics,
        title: 'Movement Path Heatmap Visualization',
        desc: 'Visualize concentrated activity areas during training as heatmaps for intuitive assessment.',
      },
      {
        icon: IconRoute,
        title: 'Automatic Training Path Logging',
        desc: 'Automatically record firefighters’ movement paths for objective, data-based evaluation.',
      },
      {
        icon: IconLayers,
        title: 'Digital Site Map Implementation',
        desc: 'Precisely replicate the actual training site structure as a digital map to maximize consistency.',
      },
      {
        icon: IconLayers,
        title: '3D-Based Training Management',
        desc: 'Use 3D models to clearly visualize training progress and status.',
      },
      {
        icon: IconLocation,
        title: 'Reliable Location Data Reception',
        desc: 'Receive stable location data without signal interference even in complex environments like structures and subway cars.',
      },
      {
        icon: IconSearch,
        title: 'Linked Management with Actual Blueprints',
        desc: 'Provide a consistent tracking solution from installation blueprints to monitoring and 3D visualization.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Components',
  product: [
    {
      tag: 'workerPackage',
    },
    {
      tag: 'assetPackage',
    },
  ],

  contents: [
    {
      data: 'constructionAsset',
    },
    {
      data: 'constructionEquipment',
    },
    {
      data: 'blogRTLS',
    },
    {
      data: 'blogMedical',
    },
  ],
};
